import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";

const AuthContext = createContext(null);

/**
 * Auth state is split between two layers:
 *  1. The JWT lives in an HttpOnly cookie set by the backend — it is NOT readable here.
 *  2. Only a minimal non-sensitive flag (`authenticated`) and the place slug
 *     (used by AppHeader to build the "Моя страница" link before we hit the
 *     API) sit in localStorage. Everything else — email, display name,
 *     role — is fetched from /api/auth/me on mount so a compromised XSS
 *     payload can't just scrape it out of storage.
 *
 * The `authenticated` flag is only a UI hint: the server is the source of
 * truth and will return 401 if the cookie is missing/expired, which triggers
 * a redirect to /login.
 */
const AUTH_FLAG_KEY = "authenticated";
const PLACE_SLUG_KEY = "placeSlug";

const EMPTY_STATE = {
  authenticated: false,
  email: null,
  placeName: null,
  placeSlug: null,
  role: null,
};

export function AuthProvider({ children }) {
  const navigate = useNavigate();

  const [authState, setAuthState] = useState(() => {
    const authenticated = localStorage.getItem(AUTH_FLAG_KEY) === "true";
    // placeSlug is the one hint we keep around: the header uses it to build
    // an "open my public page" link without waiting on a /me round-trip.
    const placeSlug = authenticated ? localStorage.getItem(PLACE_SLUG_KEY) : null;
    return {
      ...EMPTY_STATE,
      authenticated,
      placeSlug,
    };
  });

  const login = useCallback((data) => {
    // data is the AuthResponse body: { email, placeName, placeSlug, role }.
    // The JWT arrived in a Set-Cookie header and is already stored by the browser.
    localStorage.setItem(AUTH_FLAG_KEY, "true");
    if (data.placeSlug) {
      localStorage.setItem(PLACE_SLUG_KEY, data.placeSlug);
    } else {
      localStorage.removeItem(PLACE_SLUG_KEY);
    }

    setAuthState({
      authenticated: true,
      email: data.email ?? null,
      placeName: data.placeName ?? null,
      placeSlug: data.placeSlug ?? null,
      role: data.role ?? null,
    });
  }, []);

  const clearLocalSession = useCallback(() => {
    localStorage.removeItem(AUTH_FLAG_KEY);
    localStorage.removeItem(PLACE_SLUG_KEY);
    // Clean up the stale keys older builds used to write so upgrading
    // users don't leave secrets sitting in storage.
    localStorage.removeItem("email");
    localStorage.removeItem("placeName");
    localStorage.removeItem("role");

    setAuthState(EMPTY_STATE);
  }, []);

  const logout = useCallback(async () => {
    try {
      // Best-effort — the server clears the HttpOnly cookie. Even if the call fails
      // (network error, already-expired session), we still wipe local state below.
      await api.post("/auth/logout", null, { silent: true });
    } catch (err) {
      // Swallow: logout must always succeed from the user's point of view.
    }
    clearLocalSession();
    navigate("/login");
  }, [clearLocalSession, navigate]);

  // After a hard reload (or a cross-tab login), we only know the user is
  // authenticated — email/placeName/role aren't in localStorage any more.
  // Pull them from /api/auth/me. If login() already set them in-memory,
  // the fetch is skipped to avoid clobbering fresh data with a race.
  const needsHydration = authState.authenticated && authState.email == null;
  useEffect(() => {
    if (!needsHydration) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/me", { silent: true });
        if (cancelled) return;
        setAuthState((prev) => ({
          ...prev,
          email: res.data.email ?? null,
          placeName: res.data.placeName ?? null,
          placeSlug: res.data.placeSlug ?? prev.placeSlug,
          role: res.data.role ?? null,
        }));
        if (res.data.placeSlug) {
          localStorage.setItem(PLACE_SLUG_KEY, res.data.placeSlug);
        }
      } catch (err) {
        // 401 interceptor already handled the redirect; swallow the rest —
        // the header will render the minimal state until the next fetch.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsHydration]);

  // Cross-tab sync: if another tab logs in or out, the `storage` event fires
  // in every OTHER tab (the originating tab receives no event). Mirror the
  // AUTH_FLAG_KEY so a logout in one tab doesn't leave a stale "Dashboard"
  // rendered in another, and so a fresh login lights up sibling tabs.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== AUTH_FLAG_KEY) {
        return;
      }
      if (event.newValue === "true") {
        // Another tab just logged in. Flip our local flag; the profile
        // refresh effect above picks it up and pulls fresh details.
        setAuthState({
          ...EMPTY_STATE,
          authenticated: true,
          placeSlug: localStorage.getItem(PLACE_SLUG_KEY),
        });
      } else {
        // Logout or explicit removal elsewhere — drop local state.
        // Don't call the logout API (the other tab already did) and don't
        // navigate if the user is on a public page.
        setAuthState(EMPTY_STATE);
        if (window.location.pathname.startsWith("/dashboard")) {
          navigate("/login");
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
